import type { FormEvent, RefObject } from 'react'
import { ADDRESS_PATHS } from './addresses'
import type { IconName } from './icons'
import { ORDER_PATHS } from './orders'
import { ROUTES } from './routes'

export const AUTH_MESSAGES = {
  otpSent: 'OTP sent. Check your phone (or server logs in local/dev).',
  loggedIn: 'Signed in successfully.',
  registered: 'Account created successfully.',
  googleMissingClient: 'Google sign-in is not configured yet.',
  googleFailed: 'Google sign-in failed. Try again.',
} as const

export const AUTH_STORAGE_KEY = 'maaliondoor.auth.v1'

export const AUTH_PATHS = {
  home: '/',
  login: '/login',
  signup: '/signup',
  privacy: ROUTES.privacy,
  profile: '/profile',
  profileRoute: 'profile',
} as const

export type AuthPendingAction = null | 'otp-request' | 'otp-verify' | 'google'

export const AUTH_VISUAL = {
  title: 'Gifts that feel personal',
  body: 'Flowers, plants, and thoughtful picks — delivered with care.',
} as const

export const AUTH_ACCOUNT = {
  label: 'Account',
  menuAria: 'Account menu',
  menuId: 'header-account-menu',
  signedInAs: 'Signed in as',
  profile: 'Profile',
  orders: 'Orders',
  addresses: 'Addresses',
  logout: 'Sign out',
  logoutIcon: 'logout',
} as const

export const AUTH_PROFILE = {
  pageTitle: 'My account',
  detailsTitle: 'Profile details',
  detailsLead: 'The details we use for delivery and account access.',
  greeting: (name: string) => `Hi, ${name}`,
  memberSince: (date: string) => `Member since ${date}`,
  nameLabel: 'Name',
  phoneLabel: 'Phone',
  emailLabel: 'Email',
  emptyValue: 'Not provided',
  emptyLoginTitle: 'Sign in to view your account',
  emptyLoginHint: 'Your profile and order history are saved to your account.',
  loginCta: 'Sign in',
  edit: 'Edit',
  editAria: (label: string) => `Edit ${label.toLowerCase()}`,
  add: 'Add',
  cancel: 'Cancel',
  save: 'Save',
  saving: 'Saving…',
  namePlaceholder: 'Your full name',
  nameRequired: 'Please enter your name.',
  nameSaved: 'Name updated.',
  nameSaveFailed: 'Could not update your name. Try again.',
  phonePlaceholder: '10-digit mobile number',
  phoneInvalid: 'Enter a valid 10-digit mobile number.',
  phoneUnchanged: 'This is already your phone number.',
  phoneOtpHint: (phone: string) => `Enter the OTP sent to ${phone}.`,
  otpPlaceholder: 'Enter 6-digit OTP',
  otpInvalid: 'Enter the OTP sent to your new number.',
  sendOtp: 'Send OTP',
  sendingOtp: 'Sending OTP…',
  resendOtp: 'Resend OTP',
  verify: 'Verify & save',
  verifying: 'Verifying…',
  phoneSaved: 'Phone number updated.',
  phoneSaveFailed: 'Could not update your phone number. Try again.',
  phoneOtpFailed: 'Could not send the OTP. Try again.',
  phoneVerifyFailed: 'Could not verify the OTP. Try again.',
} as const

export const PROFILE_FIELD = {
  name: 'name',
  phone: 'phone',
  email: 'email',
} as const

export type ProfileFieldId = (typeof PROFILE_FIELD)[keyof typeof PROFILE_FIELD]

export type ProfileEditPending = null | 'name' | 'phone' | 'otp-request' | 'otp-verify'

export const PROFILE_LIMITS = {
  nameMax: 160,
  phoneDigits: 10,
  otpMin: 4,
  otpMax: 6,
} as const

export type AuthProfileField = {
  id: ProfileFieldId
  label: string
  value: string
  editable: boolean
  hasValue: boolean
}

export type ProfileNameFormProps = {
  label: string
  value: string
  onChange: (value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  saving: boolean
  error: string | null
}

export type ProfilePhoneFormProps = {
  label: string
  value: string
  onChange: (value: string) => void
  otp: string
  onOtpChange: (value: string) => void
  otpSentTo: string | null
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onResend: () => void
  onCancel: () => void
  pending: ProfileEditPending
  error: string | null
}

export const AUTH_KEYBOARD = {
  escape: 'Escape',
} as const

export type AuthAccountLink = {
  id: string
  label: string
  to: string
  icon: IconName
}

export const AUTH_ACCOUNT_LINKS: readonly AuthAccountLink[] = [
  { id: 'profile', label: AUTH_ACCOUNT.profile, to: AUTH_PATHS.profile, icon: 'user' },
  { id: 'orders', label: AUTH_ACCOUNT.orders, to: ORDER_PATHS.list, icon: 'package' },
  { id: 'addresses', label: AUTH_ACCOUNT.addresses, to: ADDRESS_PATHS.list, icon: 'pin' },
]

export const AUTH_LOADING = {
  sendingOtp: 'Sending OTP…',
  verifyingOtp: 'Verifying…',
  google: 'Signing in with Google…',
  settings: 'Loading sign-in options…',
} as const

export const AUTH_LOGIN = {
  title: 'Login',
  lead: 'Sign in with your mobile number',
  leadOtp: 'Sign in with your mobile number',
  leadGoogle: 'Sign in with Google',
  leadBoth: 'Sign in with Google or your mobile number',
  mobileLabel: 'Mobile number',
  mobilePlaceholder: '10-digit mobile number',
  otpLabel: 'OTP',
  otpPlaceholder: 'Enter 6-digit OTP',
  sendOtp: 'Send OTP',
  resendOtp: 'Resend OTP',
  submitMobile: 'Verify & sign in',
  googleLabel: 'Continue with Google',
  divider: 'or',
  privacyBefore: 'By continuing, you agree to our',
  privacyLink: 'Privacy Policy',
  privacyAfter: '.',
  switchPrompt: 'New to maaliondoor?',
  switchAction: 'Create an account',
  countryCode: '+91',
} as const

export const AUTH_SIGNUP = {
  title: 'Create Account',
  lead: 'Join with your mobile number',
  leadOtp: 'Join with your mobile number',
  leadGoogle: 'Create an account with Google',
  leadBoth: 'Join with Google or your mobile number',
  mobileLabel: 'Mobile number',
  mobilePlaceholder: '10-digit mobile number',
  otpLabel: 'OTP',
  otpPlaceholder: 'Enter 6-digit OTP',
  sendOtp: 'Send OTP',
  resendOtp: 'Resend OTP',
  submitMobile: 'Verify & create account',
  googleLabel: 'Continue with Google',
  divider: 'or',
  privacyBefore: 'By continuing, you agree to our',
  privacyLink: 'Privacy Policy',
  privacyAfter: '.',
  switchPrompt: 'Already have an account?',
  switchAction: 'Sign in',
  countryCode: '+91',
} as const

export const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

export const GOOGLE_AUTH_ERROR = {
  missingClient: 'MISSING_GOOGLE_CLIENT_ID',
  sdkUnavailable: 'GOOGLE_SDK_UNAVAILABLE',
  noCredential: 'GOOGLE_NO_CREDENTIAL',
  promptDismissed: 'GOOGLE_PROMPT_DISMISSED',
} as const

export const GIS_INIT_OPTIONS = {
  autoSelect: false,
  cancelOnTapOutside: true,
  uxMode: 'popup',
} as const

export const GOOGLE_BUTTON_OPTIONS = {
  type: 'standard',
  theme: 'outline',
  size: 'large',
  text: 'continue_with',
  shape: 'rectangular',
  logoAlignment: 'left',
  minWidth: 280,
} as const

export type GoogleButtonProps = {
  label: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}

export type GoogleSignInHostProps = {
  hostRef: RefObject<HTMLDivElement | null>
}
